import React from 'react';
import { connect } from 'react-redux';
import './Indicators.scss';
import { REDUCER_NAME } from './reducer';
import Table from '../../../common/table/Table';
import PropTypes from 'prop-types';
import Const from './Const';
import { getDataFollowKeyByDot } from '../../../helpers/Common';
import IndicatorsSearch from './IndicatorsSearch';
import { I18n } from 'react-redux-i18n';

const MIN_RSI = 30;
const MAX_RSI = 70;
const unAvailable = '--';

const getStyle = val => {
  switch (val) {
    case Const.BULLISH:
    case Const.STRONG_BULLISH:
      return 'text-s-color-5';
    case Const.BEARISH:
    case Const.STRONG_BEARISH:
      return 'text-s-color-3';
    case Const.NEUTRAL:
      return 'text-s-color-4';
    default:
      return unAvailable;
  }
};

const getText = val => {
  const mapValue = {
    [Const.BULLISH]: 'common.bullish',
    [Const.BEARISH]: 'common.bearish',
    [Const.NEUTRAL]: 'common.neutral',
    [Const.STRONG_BULLISH]: 'common.strongBullish',
    [Const.STRONG_BEARISH]: 'common.strongBearish',
  };

  return mapValue[val] ? I18n.t(mapValue[val]) : '--';
};

const getRsi = (rsi, oldRsi) => {
  if (rsi < MIN_RSI && rsi > oldRsi) {
    return Const.BULLISH;
  }
  if (rsi > MAX_RSI && rsi < oldRsi) {
    return Const.BEARISH;
  }
  return Const.NEUTRAL;
};

const getCmf = cmf => {
  if (cmf > 0) {
    return Const.BULLISH;
  }
  if (cmf < 0) {
    return Const.BEARISH;
  }
  return Const.NEUTRAL;
};

const getRoc = (roc, oldRoc) => {
  if (roc > 0.3 && roc < oldRoc) {
    return Const.BULLISH;
  }
  if (roc < -0.3 && roc > oldRoc) {
    return Const.BEARISH;
  }
  return Const.NEUTRAL;
};

const getIndicator = (valRsi, valCmf, valRoc) => {
  let countBullish = 0;
  let countBearish = 0;
  if (valRsi === Const.BULLISH) {
    countBullish++;
  }
  if (valRoc === Const.BULLISH) {
    countBullish++;
  }
  if (valCmf === Const.BULLISH) {
    countBullish++;
  }

  if (valRsi === Const.BEARISH) {
    countBearish++;
  }
  if (valCmf === Const.BEARISH) {
    countBearish++;
  }
  if (valRoc === Const.BEARISH) {
    countBearish++;
  }

  if (countBullish > countBearish) {
    return Const.BULLISH;
  }
  if (countBullish < countBearish) {
    return Const.BEARISH;
  }
  return Const.NEUTRAL;
};

const getSummary = (valMoving, valIndicator) => {
  let countBullish = 0;
  let countBearish = 0;
  let countNeutral = 0;

  if (valMoving === Const.BULLISH) {
    countBullish++;
  }
  if (valIndicator === Const.BULLISH) {
    countBullish++;
  }
  if (valMoving === Const.BEARISH) {
    countBearish++;
  }
  if (valIndicator === Const.BEARISH) {
    countBearish++;
  }
  if (valMoving === Const.NEUTRAL) {
    countNeutral++;
  }
  if (valIndicator === Const.NEUTRAL) {
    countNeutral++;
  }
  if (countBullish === 1 && countBearish === 1) {
    return Const.NEUTRAL;
  }
  if (countBullish === 2) {
    return Const.STRONG_BULLISH;
  }
  if (countBearish === 2) {
    return Const.STRONG_BEARISH;
  }
  if (countBullish === 1 && countNeutral === 1) {
    return Const.BULLISH;
  }
  if (countBearish === 1 && countNeutral === 1) {
    return Const.BEARISH;
  }
  return unAvailable;
};

const getMoving = (matchPrice, ma5) => {
  return matchPrice > ma5 ? Const.BULLISH : Const.BEARISH;
};

const getSchema = indicatorSearch => {
  return Object.keys(Const.listColumnTableVisibility).map(item2 => {
    const key = Const.listColumnTableVisibility[item2];
    const title = `watchListTechnical.listTitleTable.${item2}`;
    const result = {
      key,
      title,
    };

    if (key === Const.listColumnTableVisibility.TICKER) {
      return {
        ...result,
        isString: true,
        valueFromItem: item => item.organCode,
        tdClassName: 'bg-blue border-bottom-ex bor-ta-color-4',
        thStyle: {
          width: '140px',
        },
        render: (text, item) => {
          const currentSearch = indicatorSearch[item.id];
          return (
            <IndicatorsSearch
              indicatorId={item.id}
              currentSearch={currentSearch}
            />
          );
        },
      };
    }

    if (key === Const.listColumnTableVisibility.TYPE) {
      return {
        disableSort: true,
        ...result,
        thStyle: { textAlign: 'left' },
        render: (text, item) => {
          return (
            <div className="text-left">
              <div className="bg-blue p-8">
                {item.daily !== null
                  ? I18n.t(`tasignals.indicatorType.${Const.MOVING_AVERAGE}`)
                  : unAvailable}
              </div>
              <div className="bg-blue p-8">
                {item.daily !== null
                  ? I18n.t(`tasignals.indicatorType.${Const.INDICATORS}`)
                  : unAvailable}
              </div>
              <div className="bg-grey p-8 border-bottom bor-p-color-3 bg-ta-color-8 font-weight-bold">
                {item.daily !== null
                  ? I18n.t(`tasignals.indicatorType.${Const.SUMMARY}`)
                  : unAvailable}
              </div>
            </div>
          );
        },
      };
    }

    if (key === Const.listColumnTableVisibility._15MINUTES) {
      return {
        disableSort: true,
        ...result,
        thStyle: { textAlign: 'left' },
        render: (text, item) => {
          const valMoving15Minutes =
            item.daily === null
              ? '-'
              : getMoving(
                  getDataFollowKeyByDot(
                    item,
                    Const.listColumnTable.MATCH_PRICE,
                  ),
                  getDataFollowKeyByDot(
                    item,
                    Const.listColumnTable._15MINUTES_MA5,
                  ),
                );
          const valIndicator15Minutes =
            item.daily === null
              ? '-'
              : getIndicator(
                  getRsi(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable._15MINUTES_RSI,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable._15MINUTES_OLD_RSI,
                    ),
                  ),
                  getCmf(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable._15MINUTES_CMF,
                    ),
                  ),
                  getRoc(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable._15MINUTES_ROC,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable._15MINUTES_OLD_ROC,
                    ),
                  ),
                );
          return (
            <div className="text-left">
              <div className="bg-blue p-8">{getText(valMoving15Minutes)}</div>
              <div className="bg-blue p-8">
                {getText(valIndicator15Minutes)}
              </div>
              <div className="bg-grey p-8 border-bottom bor-p-color-3 bg-ta-color-8 font-weight-bold">
                <div
                  className={getStyle(
                    getSummary(valMoving15Minutes, valIndicator15Minutes),
                  )}
                >
                  {getText(
                    getSummary(valMoving15Minutes, valIndicator15Minutes),
                  )}
                </div>
              </div>
            </div>
          );
        },
      };
    }

    if (key === Const.listColumnTableVisibility.HOURLY) {
      return {
        disableSort: true,
        ...result,
        thStyle: { textAlign: 'left' },
        render: (text, item) => {
          const valMovingHourly =
            item.daily === null
              ? '-'
              : getMoving(
                  getDataFollowKeyByDot(
                    item,
                    Const.listColumnTable.MATCH_PRICE,
                  ),
                  getDataFollowKeyByDot(item, Const.listColumnTable.DAILY_MA5),
                );
          const valIndicatorHourly =
            item.daily === null
              ? '-'
              : getIndicator(
                  getRsi(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.HOURLY_RSI,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.HOURLY_OLD_RSI,
                    ),
                  ),
                  getCmf(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.HOURLY_CMF,
                    ),
                  ),
                  getRoc(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.HOURLY_ROC,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.HOURLY_OLD_ROC,
                    ),
                  ),
                );
          return (
            <div className="text-left">
              <div className="bg-blue p-8">{getText(valMovingHourly)}</div>
              <div className="bg-blue p-8">{getText(valIndicatorHourly)}</div>
              <div className="bg-grey p-8 border-bottom bor-p-color-3 bg-ta-color-8 font-weight-bold">
                <div
                  className={getStyle(
                    getSummary(valMovingHourly, valIndicatorHourly),
                  )}
                >
                  {getText(getSummary(valMovingHourly, valIndicatorHourly))}
                </div>
              </div>
            </div>
          );
        },
      };
    }

    if (key === Const.listColumnTableVisibility.DAILY) {
      return {
        disableSort: true,
        ...result,
        thStyle: { textAlign: 'left' },
        render: (text, item) => {
          const valMovingDaily =
            item.daily === null
              ? '-'
              : getMoving(
                  getDataFollowKeyByDot(
                    item,
                    Const.listColumnTable.MATCH_PRICE,
                  ),
                  getDataFollowKeyByDot(item, Const.listColumnTable.DAILY_MA5),
                );
          const valIndicatorDaily =
            item.daily === null
              ? '-'
              : getIndicator(
                  getRsi(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.DAILY_RSI,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.DAILY_OLD_RSI,
                    ),
                  ),
                  getCmf(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.DAILY_CMF,
                    ),
                  ),
                  getRoc(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.DAILY_ROC,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.DAILY_OLD_ROC,
                    ),
                  ),
                );
          return (
            <div className="text-left">
              <div className="bg-blue p-8">{getText(valMovingDaily)}</div>
              <div className="bg-blue p-8">{getText(valIndicatorDaily)}</div>
              <div className="bg-grey p-8 border-bottom bor-p-color-3 bg-ta-color-8 font-weight-bold">
                <div
                  className={getStyle(
                    getSummary(valMovingDaily, valIndicatorDaily),
                  )}
                >
                  {getText(getSummary(valMovingDaily, valIndicatorDaily))}
                </div>
              </div>
            </div>
          );
        },
      };
    }

    if (key === Const.listColumnTableVisibility.WEEKLY) {
      return {
        disableSort: true,
        ...result,
        thStyle: { textAlign: 'left' },
        render: (text, item) => {
          const valMovingWeekly =
            item.daily === null
              ? '-'
              : getMoving(
                  getDataFollowKeyByDot(
                    item,
                    Const.listColumnTable.MATCH_PRICE,
                  ),
                  getDataFollowKeyByDot(item, Const.listColumnTable.WEEKLY_MA5),
                );
          const valIndicatorWeekly =
            item.daily === null
              ? '-'
              : getIndicator(
                  getRsi(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.WEEKLY_RSI,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.WEEKLY_OLD_RSI,
                    ),
                  ),
                  getCmf(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.WEEKLY_CMF,
                    ),
                  ),
                  getRoc(
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.WEEKLY_ROC,
                    ),
                    getDataFollowKeyByDot(
                      item,
                      Const.listColumnTable.WEEKLY_OLD_ROC,
                    ),
                  ),
                );
          return (
            <div className="text-left">
              <div className="bg-blue p-8">{getText(valMovingWeekly)}</div>
              <div className="bg-blue p-8">{getText(valIndicatorWeekly)}</div>
              <div className="bg-grey p-8 border-bottom bor-p-color-3 bg-ta-color-8 font-weight-bold">
                <div
                  className={getStyle(
                    getSummary(valMovingWeekly, valIndicatorWeekly),
                  )}
                >
                  {getText(getSummary(valMovingWeekly, valIndicatorWeekly))}
                </div>
              </div>
            </div>
          );
        },
      };
    }

    return {
      ...result,
    };
  });
};

const getDataFromRedux = state => state[REDUCER_NAME].indicatorById;

const IndicatorsTable = ({ indicatorIds, isLoading, indicatorSearch }) => (
  <div className="technical-wrapper d-flex flex-fill">
    <Table
      ids={indicatorIds}
      isLoading={isLoading}
      resizable={true}
      columnDraggable={false}
      rowDraggable={false}
      hideColumns={[]}
      getDataFromRedux={getDataFromRedux}
      schema={getSchema(indicatorSearch)}
      isTrHover={false}
    />
  </div>
);

IndicatorsTable.propTypes = {
  indicatorIds: PropTypes.array.isRequired,
  indicatorSearch: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  ids: state[REDUCER_NAME].listTickerIndicators,
  listTickerIndicatorsByTicker:
    state[REDUCER_NAME].listTickerIndicatorsByTicker,
  isLoading: state[REDUCER_NAME].isLoading,
  indicatorSearch: state[REDUCER_NAME].indicatorSearch,
  indicatorIds: state[REDUCER_NAME].indicatorIds,
  i18n: state.i18n,
});

export default connect(mapStateToProps)(IndicatorsTable);
